<template>
  <section class="py-16 md:py-24 bg-gray-50">
    <div class="w-[90%] mx-auto">
      <!-- Section Header -->
      <div class="text-center space-y-4 mb-12">
        <h2 class="text-2xl md:text-4xl font-bold text-gray-900">
          {{ $t('howItWorksAdmin.title') }}
        </h2>
        <div class="flex justify-center">
          <div class="h-1 w-16 bg-[#009B9D] rounded-full"></div>
        </div>
        <p class="text-gray-600 max-w-2xl mx-auto text-sm md:text-base">
          {{ $t('howItWorksAdmin.subtitle') }}
        </p>
      </div>

      <!-- Content Grid - Reversed layout -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
        <!-- Desktop/Tablet Mockup - Left side -->
        <div class="hidden lg:flex justify-center items-center order-2 lg:order-1">
          <div class="relative">
            <!-- Decorative Elements -->
            <div class="absolute -top-8 -left-8 w-32 h-32 bg-[#009B9D]/10 rounded-full blur-3xl"></div>
            <div class="absolute -bottom-8 -right-8 w-40 h-40 bg-[#009B9D]/5 rounded-full blur-3xl"></div>
            
            <!-- Laptop Frame - Proportions naturelles -->
            <div class="relative z-10">
              <!-- Screen -->
              <div class="w-[480px] bg-[#1a2e3b] rounded-t-xl p-2 shadow-2xl">
                <div class="w-full aspect-[16/10] bg-white rounded-lg overflow-hidden">
                  <transition
                    mode="out-in"
                    enter-active-class="transition-all duration-500 ease-out"
                    enter-from-class="opacity-0"
                    enter-to-class="opacity-100"
                    leave-active-class="transition-all duration-300 ease-in"
                    leave-from-class="opacity-100"
                    leave-to-class="opacity-0"
                  >
                    <img 
                      :key="activeStep"
                      :src="`/images/${steps[activeStep].image}`" 
                      :alt="$t(steps[activeStep].titleKey)"
                      class="w-full h-full object-cover object-top"
                    />
                  </transition>
                </div>
              </div>
              <!-- Laptop Base -->
              <div class="w-[520px] h-4 bg-[#2a3f4d] rounded-b-xl mx-auto -mt-0.5 shadow-lg"></div>
              <div class="w-[180px] h-1.5 bg-[#3a4f5d] rounded-b-lg mx-auto"></div>
            </div>

            <!-- Floating Stats Cards -->
            <div 
              class="absolute -right-6 top-6 bg-white rounded-xl shadow-xl p-4 animate-float"
              style="animation-delay: 0s;"
            >
              <div class="flex items-center space-x-3">
                <div class="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <svg class="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
                  </svg>
                </div>
                <div>
                  <p class="text-xs text-gray-500">Revenus du jour</p>
                  <p class="font-bold text-gray-800">+45 000 XAF</p>
                </div>
              </div>
            </div>

            <div 
              class="absolute -left-6 bottom-16 bg-white rounded-xl shadow-xl p-4 animate-float"
              style="animation-delay: 0.5s;"
            >
              <div class="flex items-center space-x-3">
                <div class="w-10 h-10 bg-[#009B9D]/20 rounded-full flex items-center justify-center">
                  <svg class="w-5 h-5 text-[#009B9D]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"/>
                  </svg>
                </div>
                <div>
                  <p class="text-xs text-gray-500">Commandes</p>
                  <p class="font-bold text-gray-800">12 en cours</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Steps List - Right side -->
        <div class="space-y-4 order-1 lg:order-2">
          <div 
            v-for="(step, index) in steps" 
            :key="index"
            @click="activeStep = index"
            class="group cursor-pointer"
          >
            <!-- Step Card -->
            <div 
              class="relative p-5 md:p-6 rounded-2xl transition-all duration-500 ease-out"
              :class="[
                activeStep === index 
                  ? 'bg-white shadow-lg shadow-[#009B9D]/10 scale-[1.02]' 
                  : 'bg-white/50 hover:bg-white'
              ]"
            >
              <!-- Active Indicator Bar -->
              <div 
                class="absolute right-0 top-1/2 -translate-y-1/2 w-1 rounded-l-full bg-[#009B9D] transition-all duration-500"
                :class="activeStep === index ? 'h-3/4 opacity-100' : 'h-0 opacity-0'"
              ></div>

              <div class="flex items-start space-x-4">
                <!-- Step Icon -->
                <div 
                  class="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300"
                  :class="[
                    activeStep === index 
                      ? 'bg-[#009B9D] text-white shadow-lg shadow-[#009B9D]/30' 
                      : 'bg-[#009B9D]/10 text-[#009B9D] group-hover:bg-[#009B9D]/20'
                  ]"
                >
                  <div v-html="step.icon"></div>
                </div>

                <!-- Step Content -->
                <div class="flex-1">
                  <h3 
                    class="font-semibold text-lg transition-colors duration-300"
                    :class="activeStep === index ? 'text-[#009B9D]' : 'text-gray-900 group-hover:text-[#009B9D]'"
                  >
                    {{ $t(step.titleKey) }}
                  </h3>
                  <p 
                    class="mt-1 text-sm transition-all duration-300"
                    :class="activeStep === index ? 'text-gray-700' : 'text-gray-500'"
                  >
                    {{ $t(step.descKey) }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Mobile/Tablet view -->
        <div class="lg:hidden flex justify-center mt-8 order-3">
          <div class="w-full max-w-md rounded-xl overflow-hidden shadow-xl border-4 border-gray-800">
            <transition
              mode="out-in"
              enter-active-class="transition-opacity duration-300"
              enter-from-class="opacity-0"
              enter-to-class="opacity-100"
              leave-active-class="transition-opacity duration-200"
              leave-from-class="opacity-100"
              leave-to-class="opacity-0"
            >
              <img 
                :key="activeStep"
                :src="`/images/${steps[activeStep].image}`" 
                :alt="$t(steps[activeStep].titleKey)"
                class="w-full h-auto object-cover"
              />
            </transition>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue'

export default {
  name: 'HowItWorkAdmin',
  setup() {
    const activeStep = ref(0)
    let interval = null

    const steps = [
      {
        titleKey: 'howItWorksAdmin.steps.dashboard.title',
        descKey: 'howItWorksAdmin.steps.dashboard.description',
        image: 'dashboard.png',
        icon: '<svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"/></svg>'
      },
      {
        titleKey: 'howItWorksAdmin.steps.orders.title',
        descKey: 'howItWorksAdmin.steps.orders.description',
        image: 'orders.png',
        icon: '<svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"/></svg>'
      },
      {
        titleKey: 'howItWorksAdmin.steps.customers.title',
        descKey: 'howItWorksAdmin.steps.customers.description',
        image: 'customers.png',
        icon: '<svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/></svg>'
      },
      {
        titleKey: 'howItWorksAdmin.steps.stats.title',
        descKey: 'howItWorksAdmin.steps.stats.description',
        image: 'statistics.png',
        icon: '<svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/></svg>'
      }
    ]

    const startAutoCycle = () => {
      interval = setInterval(() => {
        activeStep.value = (activeStep.value + 1) % steps.length
      }, 4000)
    }

    const stopAutoCycle = () => {
      if (interval) {
        clearInterval(interval)
        interval = null
      }
    }

    onMounted(() => {
      startAutoCycle()
    })

    onUnmounted(() => {
      stopAutoCycle()
    })

    return {
      activeStep,
      steps
    }
  }
}
</script>

<style scoped>
@keyframes float {
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
}

.animate-float {
  animation: float 3s ease-in-out infinite;
}
</style>